import React, { useState, useEffect, useRef } from 'react';
import {
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CButton,
    CFormInput,
    CSpinner,
    CTable,
    CTableBody,
    CTableRow,
    CTableDataCell
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import {
    cilBug,
    cilImage,
    cilCheck,
    cilTrash
} from '@coreui/icons';
import axios from 'axios';
import SettingsNeuralModule from './SettingsNeuralModule';
import { toast } from "react-toastify";

const AddDefectsModal = ({ visible, onClose, objectData, preselectedDefect }) => {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [fullImageSrc, setFullImageSrc] = useState(null);
    const [fullImageLoading, setFullImageLoading] = useState(false);
    
    const [rotation, setRotation] = useState(0);
    const [markerColor, setMarkerColor] = useState('#FF0000');
    
    const [settingsModalVisible, setSettingsModalVisible] = useState(false);
    
    // Списки дефектов
    const [selectedDefects, setSelectedDefects] = useState([]);
    const [selectedDefectForDrawing, setSelectedDefectForDrawing] = useState(null);
    
    // Режимы и состояние мыши
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    
    // Рамки
    const [savedBoxes, setSavedBoxes] = useState([]); // Хранит {id, x, y, w, h (в %), color, defect}
    const [selectedBoxId, setSelectedBoxId] = useState(null); // ID выделенной рамки
    
    // Временные состояния для манипуляций
    const [tempBox, setTempBox] = useState(null); // Рисуемая в данный момент рамка
    const [drawStartPos, setDrawStartPos] = useState(null); // Начальная точка рисования
    const [draggingBoxId, setDraggingBoxId] = useState(null); // ID перетаскиваемой рамки
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // Смещение курсора относительно угла рамки
    const [resizingBoxId, setResizingBoxId] = useState(null); // ID изменяемой рамки
    const [resizeHandle, setResizeHandle] = useState(null); // Тип ручки (nw, se, n, etc.)
    
    const [isSaving, setIsSaving] = useState(false);
    
    const imageRef = useRef(null);
    const containerRef = useRef(null);
    const token = localStorage.getItem('token');
    
    const safeOnClose = typeof onClose === 'function' ? onClose : () => { };

    // --- Загрузка классов ---
    const fetchFirstLevelClass = async (classId) => {
        try {
            const response = await axios.get(
                `/api/neural_network_module/firstlevelclasses/?id=${classId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                return response.data;
            } else if (Array.isArray(response.data) && response.data.length > 0) {
                return response.data[0];
            }
            return null;
        } catch (error) {
            console.error(`Ошибка загрузки класса первого уровня с ID ${classId}:`, error);
            return null;
        }
    };

    const fetchSecondLevelClass = async (classId) => {
        try {
            const response = await axios.get(
                `/api/neural_network_module/secondlevelclasses/?id=${classId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                return response.data;
            } else if (Array.isArray(response.data) && response.data.length > 0) {
                return response.data[0];
            }
            return null;
        } catch (error) {
            console.error(`Ошибка загрузки класса второго уровня с ID ${classId}:`, error);
            return null;
        }
    };

    const handleDefectSelected = async (item) => {
        try {
            const firstLevelData = await fetchFirstLevelClass(item.ClassID_FirstLevel);
            const secondLevelData = await fetchSecondLevelClass(item.ClassID_SecondLevel);

            if (!firstLevelData || !secondLevelData) {
                toast.error('Не удалось загрузить данные классов');
                return;
            }

            const fullDefectData = {
                ...item,
                firstLevelClass: firstLevelData,
                secondLevelClass: secondLevelData,
                id: Date.now() + Math.random() 
            };

            if (!selectedDefects.some(d => d.ID === fullDefectData.ID)) {
                setSelectedDefects(prev => [...prev, fullDefectData]);
            }

            setSelectedDefectForDrawing(fullDefectData);
            setIsDrawingMode(true);
            setSettingsModalVisible(false);
        } catch (error) {
            console.error('Ошибка при обработке выбранного дефекта:', error);
            toast.error('Произошла ошибка при загрузке данных дефекта');
        }
    };

    // --- Обработка преселекта ---
    useEffect(() => {
        if (!preselectedDefect || !visible) return;

        const processPreselectedDefect = async () => {
            try {
                const firstLevelData = await fetchFirstLevelClass(preselectedDefect.ClassID_FirstLevel);
                const secondLevelData = await fetchSecondLevelClass(preselectedDefect.ClassID_SecondLevel);

                if (!firstLevelData || !secondLevelData) {
                    toast.error('Не удалось загрузить данные классов для предварительно выбранного дефекта');
                    return;
                }

                const fullDefectData = {
                    ...preselectedDefect,
                    firstLevelClass: firstLevelData,
                    secondLevelClass: secondLevelData,
                    id: Date.now() + Math.random()
                };

                setSelectedDefects(prev => {
                    if (!prev.some(d => d.ID === fullDefectData.ID)) {
                        return [...prev, fullDefectData];
                    }
                    return prev;
                });

                setSelectedDefectForDrawing(fullDefectData);
                setIsDrawingMode(true);
            } catch (error) {
                console.error('Ошибка при обработке предварительно выбранного дефекта:', error);
            }
        };

        processPreselectedDefect();
    }, [preselectedDefect, visible]);

    // --- Загрузка изображений ---
    const fetchImagesWithoutDefects = async () => {
        try {
            const response = await axios.get(
                `/api/neural_network_module/${objectData.Id}/images-without-defects/`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data || [];
        } catch (error) {
            console.error('Ошибка загрузки изображений без дефектов:', error);
            return [];
        }
    };

    const getFileNameFromPath = (filePath) => {
        return filePath.split('\\').pop().split('/').pop();
    };

    const getFullSizePath = (thumbnailPath) => {
        return thumbnailPath.replace('/Фото/Thumbnails/', '/Фото/');
    };

    const loadThumbnailsForImages = async (imageList) => {
        if (!objectData?.LDoc || imageList.length === 0) return [];
        setLoading(true);
        const THUMBNAILS_ROOT = '/Фото/Thumbnails';
        const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

        const getMimeType = (filename) => {
            const ext = filename?.split('.').pop()?.toLowerCase();
            switch (ext) {
                case 'png': return 'image/png';
                case 'gif': return 'image/gif';
                case 'webp': return 'image/webp';
                default: return 'image/jpeg';
            }
        };

        try {
            const response = await axios.get(`/api/ldoc/${objectData.LDoc}/workdir/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const files = response.data || [];
            
            const thumbnailMap = {};
            files.forEach(file => {
                if (!file.isDirectory && file.path.startsWith(THUMBNAILS_ROOT)) {
                    const ext = file.name?.split('.').pop()?.toLowerCase();
                    if (imageExtensions.includes(ext)) {
                        const fileName = getFileNameFromPath(file.path);
                        thumbnailMap[fileName] = file;
                    }
                }
            });

            const photoRequests = imageList.map(async (imageItem) => {
                const fileName = getFileNameFromPath(imageItem.FilePath);
                const thumbnailFile = thumbnailMap[fileName];

                if (!thumbnailFile) return null;

                try {
                    const previewUrl = `/api/ldoc/${objectData.LDoc}/workdir/preview/?path=${encodeURIComponent(thumbnailFile.path)}`;
                    const res = await axios.get(previewUrl, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob',
                    });

                    if (!res.data || res.data.size === 0) return null;

                    const mimeType = getMimeType(thumbnailFile.name);
                    const blob = new Blob([res.data], { type: mimeType });
                    const url = URL.createObjectURL(blob);

                    return {
                        path: thumbnailFile.path,
                        url,
                        name: thumbnailFile.name,
                        fullPath: imageItem.FilePath 
                    };
                } catch (err) {
                    console.error('Ошибка загрузки миниатюры:', err.message);
                    return null;
                }
            });

            const results = await Promise.all(photoRequests);
            const validPhotos = results.filter(photo => photo !== null);
            setPhotos(validPhotos);

            if (validPhotos.length > 0) {
                setSelectedPhoto(validPhotos[0]);
                loadFullImage(validPhotos[0]);
            } else {
                setSelectedPhoto(null);
                setFullImageSrc(null);
            }

            return validPhotos;
        } catch (err) {
            console.error('Ошибка загрузки миниатюр:', err);
            setPhotos([]);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const loadFullImage = (photo) => {
        if (fullImageSrc) URL.revokeObjectURL(fullImageSrc);
        setFullImageLoading(true);
        setFullImageSrc(null);
        setSavedBoxes([]); // Очищаем рамки при смене фото
        setSelectedBoxId(null);
        
        const fullSizePath = getFullSizePath(photo.path);
        const fullImageUrl = `/api/ldoc/${objectData.LDoc}/workdir/preview/?path=${encodeURIComponent(fullSizePath)}`;

        axios
            .get(fullImageUrl, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            })
            .then((res) => {
                if (!res.data || res.data.size === 0) throw new Error('Empty response');
                const blob = new Blob([res.data], { type: res.data.type || 'image/jpeg' });
                const url = URL.createObjectURL(blob);
                setFullImageSrc(url);
            })
            .catch((err) => {
                console.error('Ошибка загрузки полного изображения:', err);
                setFullImageSrc(null);
            })
            .finally(() => {
                setFullImageLoading(false);
            });
    };

    // --- Обработчики UI ---

    const handleThumbnailClick = (photo) => {
        setSelectedPhoto(photo);
        setRotation(0);
        loadFullImage(photo);
    };

    const handleAddDefect = () => {
        setSettingsModalVisible(true);
    };

    const handleDrawBox = () => {
        if (selectedDefectForDrawing) {
            setIsDrawingMode(prev => !prev);
            // Сбрасываем выделение при переключении режима
            setSelectedBoxId(null);
            setResizingBoxId(null);
            setResizeHandle(null);
        } else {
            toast.warn("Сначала выберите дефект из списка слева.");
        }
    };

    const removeDefect = (id) => {
        setSelectedDefects(prev => prev.filter(d => d.id !== id));
        if (selectedDefectForDrawing?.id === id) {
            setSelectedDefectForDrawing(null);
            setIsDrawingMode(false);
        }
    };

    const handleDefectRowClick = (defect) => {
        setSelectedDefectForDrawing(defect);
        setIsDrawingMode(true);
    };

    const deleteSelectedBox = () => {
        if (selectedBoxId) {
            setSavedBoxes(prev => prev.filter(box => box.id !== selectedBoxId));
            setSelectedBoxId(null);
            setResizingBoxId(null);
            setResizeHandle(null);
        }
    };

    // Обработка клавиши Delete
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBoxId) {
                deleteSelectedBox();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectedBoxId]);

    useEffect(() => {
        if (!visible || !objectData?.Id) return;
        const loadImages = async () => {
            const images = await fetchImagesWithoutDefects();
            await loadThumbnailsForImages(images);
        };
        loadImages();
        return () => {
            if (fullImageSrc) URL.revokeObjectURL(fullImageSrc);
        };
    }, [visible, objectData?.Id, token]);

    // --- Вспомогательные функции для мыши ---

    const getScaleFactors = () => {
        if (!imageRef.current) return { scaleX: 1, scaleY: 1, naturalWidth: 0, naturalHeight: 0 };
        const img = imageRef.current;
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        const rect = img.getBoundingClientRect();
        const displayWidth = rect.width;
        const displayHeight = rect.height;
        const scaleX = displayWidth / naturalWidth;
        const scaleY = displayHeight / naturalHeight;
        return { scaleX, scaleY, naturalWidth, naturalHeight };
    };

    const getResizeHandle = (box, x, y, img) => {
        if (!img) return null;
        const scaleX = img.offsetWidth / img.naturalWidth;
        const scaleY = img.offsetHeight / img.naturalHeight;

        // Переводим % в пиксели на экране
        const boxLeft = (box.x / 100) * img.offsetWidth;
        const boxTop = (box.y / 100) * img.offsetHeight;
        const boxRight = boxLeft + (box.w / 100) * img.offsetWidth;
        const boxBottom = boxTop + (box.h / 100) * img.offsetHeight;
        const boxWidth = (box.w / 100) * img.offsetWidth;
        const boxHeight = (box.h / 100) * img.offsetHeight;

        const handleSize = 10; 
        
        // Проверка попадания в углы
        if (Math.abs(x - boxLeft) <= handleSize && Math.abs(y - boxTop) <= handleSize) return 'nw';
        if (Math.abs(x - boxRight) <= handleSize && Math.abs(y - boxTop) <= handleSize) return 'ne';
        if (Math.abs(x - boxLeft) <= handleSize && Math.abs(y - boxBottom) <= handleSize) return 'sw';
        if (Math.abs(x - boxRight) <= handleSize && Math.abs(y - boxBottom) <= handleSize) return 'se';
        
        // Стороны
        if (Math.abs(x - (boxLeft + boxWidth / 2)) <= handleSize && Math.abs(y - boxTop) <= handleSize) return 'n';
        if (Math.abs(x - (boxLeft + boxWidth / 2)) <= handleSize && Math.abs(y - boxBottom) <= handleSize) return 's';
        if (Math.abs(x - boxLeft) <= handleSize && Math.abs(y - (boxTop + boxHeight / 2)) <= handleSize) return 'w';
        if (Math.abs(x - boxRight) <= handleSize && Math.abs(y - (boxTop + boxHeight / 2)) <= handleSize) return 'e';

        return null;
    };

    const findClickedBox = (x, y) => {
        if (!imageRef.current) return null;
        const img = imageRef.current;
        
        // Переводим клик (в пикселях относительно картинки) в проценты
        const xPercent = (x / img.offsetWidth) * 100;
        const yPercent = (y / img.offsetHeight) * 100;

        // Ищем рамку с конца массива (чтобы выбрать ту, что визуально сверху)
        for (let i = savedBoxes.length - 1; i >= 0; i--) {
            const box = savedBoxes[i];
            
            // Нормализуем координаты рамки для проверки
            // (x, y - левый верхний угол, w, h - положительные ширина/высота)
            const boxX = Math.min(box.x, box.x + box.w);
            const boxY = Math.min(box.y, box.y + box.h);
            const boxW = Math.abs(box.w);
            const boxH = Math.abs(box.h);

            // Проверяем попадание точки клика в прямоугольник
            if (
                xPercent >= boxX && 
                xPercent <= boxX + boxW && 
                yPercent >= boxY && 
                yPercent <= boxY + boxH
            ) {
                return box;
            }
        }
        return null;
    };

    // --- Обработчик нажатия мыши ---
    const handleMouseDown = (e) => {
        if (!imageRef.current || e.button !== 0) return;

        const img = imageRef.current;
        const rect = img.getBoundingClientRect();
        
        // Координаты клика относительно изображения в пикселях (с учетом масштаба отображения)
        // e.clientX - rect.left дает координату внутри элемента img
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        // 1. Проверяем клик по Handle (ручке ресайза), если есть выделенная рамка
        if (selectedBoxId) {
            const selectedBox = savedBoxes.find(b => b.id === selectedBoxId);
            if (selectedBox) {
                const handle = getResizeHandle(selectedBox, clickX, clickY, img);
                if (handle) {
                    setResizingBoxId(selectedBoxId);
                    setResizeHandle(handle);
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
            }
        }

        // 2. Проверяем клик по существующей рамке (для выделения/перетаскивания)
        // Проверяем ДО режима рисования, чтобы можно было выделить рамку даже в режиме рисования
        const clickedBox = findClickedBox(clickX, clickY);
        
        if (clickedBox) {
            e.preventDefault();
            e.stopPropagation();
            
            setSelectedBoxId(clickedBox.id);
            setDraggingBoxId(clickedBox.id);
            
            // Запоминаем смещение для драга
            const xPercent = (clickX / img.offsetWidth) * 100;
            const yPercent = (clickY / img.offsetHeight) * 100;
            
            // Используем фактические координаты кликнутой рамки из стейта
            setDragOffset({
                x: xPercent - clickedBox.x,
                y: yPercent - clickedBox.y
            });
            
            // Если мы в режиме рисования, но кликнули на существующую рамку - не рисуем
            return;
        }

        // 3. Если ничего не кликнули и включен режим рисования - начинаем рисовать
        if (isDrawingMode) {
            e.preventDefault();
            e.stopPropagation();
            
            // Сбрасываем выделение
            setSelectedBoxId(null);
            
            const xPercent = (clickX / img.offsetWidth) * 100;
            const yPercent = (clickY / img.offsetHeight) * 100;
            
            setDrawStartPos({ x: xPercent, y: yPercent });
            setTempBox({ x: xPercent, y: yPercent, w: 0, h: 0 });
            setIsDrawing(true);
            return;
        }

        // 4. Клик в пустое место без режима рисования - снимаем выделение
        setSelectedBoxId(null);
    };

    const handleMouseMove = (e) => {
        if (!imageRef.current) return;
        
        const img = imageRef.current;
        const rect = img.getBoundingClientRect();
        
        // Текущие координаты мыши относительно картинки в пикселях
        // Ограничиваем в пределах картинки
        let currentX = e.clientX - rect.left;
        let currentY = e.clientY - rect.top;
        
        // Clamp values
        currentX = Math.max(0, Math.min(currentX, img.offsetWidth));
        currentY = Math.max(0, Math.min(currentY, img.offsetHeight));
        
        const currentXPercent = (currentX / img.offsetWidth) * 100;
        const currentYPercent = (currentY / img.offsetHeight) * 100;

        // 1. Рисование
        if (isDrawing && tempBox && drawStartPos) {
            // Вычисляем min/max для поддержки рисования в любую сторону
            const newX = Math.min(drawStartPos.x, currentXPercent);
            const newY = Math.min(drawStartPos.y, currentYPercent);
            const newW = Math.abs(currentXPercent - drawStartPos.x);
            const newH = Math.abs(currentYPercent - drawStartPos.y);
            
            setTempBox({ x: newX, y: newY, w: newW, h: newH });
        }
        
        // 2. Перетаскивание
        else if (draggingBoxId) {
            const newX = currentXPercent - dragOffset.x;
            const newY = currentYPercent - dragOffset.y;
            
            setSavedBoxes(prev => prev.map(box => 
                box.id === draggingBoxId 
                    ? { ...box, x: newX, y: newY } 
                    : box
            ));
        }
        
        // 3. Изменение размера
        else if (resizingBoxId && resizeHandle) {
            setSavedBoxes(prev => prev.map(box => {
                if (box.id !== resizingBoxId) return box;
                
                let { x, y, w, h } = box;
                
                // Для простоты пересчитываем в процентах
                
                if (resizeHandle.includes('e')) {
                    w = currentXPercent - x;
                }
                if (resizeHandle.includes('w')) {
                    const right = x + w;
                    x = currentXPercent;
                    w = right - x;
                }
                if (resizeHandle.includes('s')) {
                    h = currentYPercent - y;
                }
                if (resizeHandle.includes('n')) {
                    const bottom = y + h;
                    y = currentYPercent;
                    h = bottom - y;
                }
                
                // Минимальный размер (например 1%)
                if (w < 1) w = 1;
                if (h < 1) h = 1;
                
                return { ...box, x, y, w, h };
            }));
        }
    };

    const handleMouseUp = () => {
        // Завершение рисования
        if (isDrawing && tempBox) {
            if (tempBox.w > 1 && tempBox.h > 1) { // Мин. размер 1%
                setSavedBoxes(prev => [...prev, {
                    ...tempBox,
                    defect: selectedDefectForDrawing?.DefectName || 'Дефект',
                    color: markerColor,
                    id: Date.now()
                }]);
                // Автоматически выделяем только что созданную рамку
                setSelectedBoxId(Date.now()); // Date.now() тот же, что и выше, т.к. синхронно
            }
            setTempBox(null);
            setIsDrawing(false);
            setDrawStartPos(null);
        }
        
        // Завершение перетаскивания и ресайза
        setDraggingBoxId(null);
        setResizingBoxId(null);
        setResizeHandle(null);
    };

    useEffect(() => {
        // Вешаем слушатели на document для корректной обработки выхода за пределы картинки при драге
        if (isDrawing || draggingBoxId || resizingBoxId) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDrawing, draggingBoxId, resizingBoxId, tempBox, drawStartPos, dragOffset, resizeHandle]);

    // --- Сохранение ---
    const fetchTaskId = async () => {
        try {
            const response = await axios.get(
                `/api/neural_network_module/task-ai/?ext_object_id=${objectData.Id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data?.task_id || null;
        } catch (error) {
            console.error('Ошибка получения TaskID:', error);
            return null;
        }
    };

    const handleSaveChanges = async () => {
        if (savedBoxes.length === 0) {
            toast.warn('Нет рамок для сохранения');
            return;
        }
        if (!selectedPhoto) {
            toast.error('Не выбрано фото');
            return;
        }
        if (!imageRef.current) {
            toast.error('Изображение не загружено');
            return;
        }

        const { naturalWidth, naturalHeight } = getScaleFactors();
        if (naturalWidth === 0 || naturalHeight === 0) {
            toast.error('Не удалось получить размеры оригинального изображения');
            return;
        }

        setIsSaving(true);
        try {
            const taskId = await fetchTaskId();
            if (!taskId) throw new Error('Не удалось получить TaskID для этого объекта');
            
            const imageSize = `${naturalWidth}x${naturalHeight}`; 
            
            if (!selectedDefectForDrawing || !selectedDefectForDrawing.firstLevelClass) {
                throw new Error('Не выбран дефект или отсутствуют данные класса');
            }

            let filePathToSave = selectedPhoto.fullPath;
            if (filePathToSave) {
                filePathToSave = filePathToSave.replace(/[\\/]Thumbnails[\\/]/i, '\\');
                filePathToSave = filePathToSave.replace(/\//g, '\\');
            }

            const firstLevelData = {
                FileName: selectedPhoto.name,
                FilePath: filePathToSave, 
                Name: selectedDefectForDrawing.firstLevelClass.ClassID, 
                IsMatch: 1,
                Path: " ", 
                ImageSize: imageSize,
                Color: markerColor,
                CoordLat: 0,
                CoordLng: 0,
                Altitude: null,
                Score: 100,
                TaskID: taskId
            };
            
            const firstLevelResponse = await axios.post(
                '/api/neural_network_module/first-level-ai/',
                firstLevelData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const firstLevelId = firstLevelResponse.data.id;
            if (!firstLevelId) throw new Error('Сервер не вернул ID созданной записи FirstLevelAI');

            const secondLevelPromises = savedBoxes.map(async (box) => {
                const x1_px = Math.round((box.x / 100) * naturalWidth);
                const y1_px = Math.round((box.y / 100) * naturalHeight);
                const x2_px = Math.round(((box.x + box.w) / 100) * naturalWidth);
                const y2_px = Math.round(((box.y + box.h) / 100) * naturalHeight);

                if (!selectedDefectForDrawing.secondLevelClass) throw new Error('Отсутствуют данные класса второго уровня');

                const secondLevelData = {
                    Name: selectedDefectForDrawing.secondLevelClass.ClassID,
                    IsMatch: 1,
                    Color: box.color,
                    CoordLat: 0,
                    CoordLng: 0,
                    Score: 100,
                    FirstLevelAIID: firstLevelId,
                    FilterThreshold: null,
                    Location: null,
                    Category: null,
                    Recommendation: null,
                    Cause: null,
                    Label: null,
                    DefectCharacter: null,
                    x1: x1_px, y1: y1_px, x2: x2_px, y2: y2_px, 
                    ReportFilePath: null,
                    idDefectTable: null,
                    TypeOutCell: null,
                    IsUnionToPhoto: null,
                    IsManual: null 
                };

                return axios.post(
                    '/api/neural_network_module/second-level-ai/',
                    secondLevelData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            });

            await Promise.all(secondLevelPromises);

            setSavedBoxes([]);
            toast.success('Данные успешно сохранены!');
            safeOnClose();
        } catch (error) {
            console.error('Ошибка сохранения данных:', error);
            toast.error('Произошла ошибка при сохранении данных: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsSaving(false);
        }
    };

    // --- Рендеринг рамок и ручек ---

    const getBoxStyle = (box) => {
        const img = imageRef.current;
        if (!img) return {};

        const containerRect = img.getBoundingClientRect();
        if (containerRect.width === 0 || containerRect.height === 0) return {};

        // В этом модальном окне rotation = 0 пока что, но если будем крутить, надо учитывать
        const angle = rotation;
        // ... (логика поворота для box если нужна, аналогично MainNeuralModal)

        // Здесь упрощенно, т.к. координаты в %, просто накладываем на контейнер
        return {
            position: 'absolute',
            left: `${box.x}%`,
            top: `${box.y}%`,
            width: `${box.w}%`,
            height: `${box.h}%`,
            border: `2px solid ${box.color}`,
            backgroundColor: 'rgba(255, 255, 0, 0.1)',
            transform: `rotate(${angle}deg)`,
            transformOrigin: 'center',
            pointerEvents: 'auto', // Чтобы ловить клики
            zIndex: 10,
            cursor: 'move',
            boxSizing: 'border-box' // Важно чтобы border не увеличивал размер
        };
    };

    const ResizeHandles = ({ boxId }) => {
        const box = savedBoxes.find(b => b.id === boxId);
        if (!box) return null;

        // Позиции ручек в процентах относительно контейнера картинки
        // x, y, w, h - это %
        const { x, y, w, h } = box;
        
        const handleStyle = (left, top, cursor) => ({
            position: 'absolute',
            left: `${left}%`,
            top: `${top}%`,
            width: '8px',
            height: '8px',
            backgroundColor: '#007bff',
            border: '1px solid white',
            borderRadius: '2px',
            transform: 'translate(-50%, -50%)',
            cursor: cursor,
            zIndex: 20
        });

        // Внимание: ручки нужно рисовать поверх картинки, но НЕ внутри рамки,
        // а в том же контейнере image-container.
        return (
            <>
                <div style={handleStyle(x, y, 'nw-resize')} />
                <div style={handleStyle(x + w, y, 'ne-resize')} />
                <div style={handleStyle(x, y + h, 'sw-resize')} />
                <div style={handleStyle(x + w, y + h, 'se-resize')} />
                
                <div style={handleStyle(x + w/2, y, 'n-resize')} />
                <div style={handleStyle(x + w/2, y + h, 's-resize')} />
                <div style={handleStyle(x, y + h/2, 'w-resize')} />
                <div style={handleStyle(x + w, y + h/2, 'e-resize')} />
            </>
        );
    };

    const SpinnerComponent = () => (
        <div style={{
            width: '32px', height: '32px', border: '3px solid #ddd',
            borderTop: '3px solid #0d6efd', borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '20px auto'
        }}></div>
    );

    return (
        <>
            <CModal visible={visible} onClose={safeOnClose} size="xl" backdrop="static" keyboard={false}>
                <CModalHeader>
                    <CModalTitle>Добавить дефекты</CModalTitle>
                </CModalHeader>
                <div style={styles.buttonBar}>
                    <CButton size="sm" onClick={handleAddDefect}>
                        <CIcon icon={cilBug} /> Добавить дефект
                    </CButton>
                    <CButton
                        size="sm"
                        onClick={handleDrawBox}
                        disabled={!selectedDefectForDrawing}
                        color={isDrawingMode ? 'primary' : 'secondary'}
                    >
                        <CIcon icon={cilImage} />
                        {isDrawingMode ? ' Отменить рисование' : ' Нарисовать рамку'}
                    </CButton>
                    <CButton 
                        size="sm" 
                        color="danger" 
                        onClick={deleteSelectedBox}
                        disabled={!selectedBoxId}
                    >
                        <CIcon icon={cilTrash} /> Удалить рамку
                    </CButton>
                    
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
                        <span style={{ marginRight: '8px', fontSize: '13px' }}>Цвет:</span>
                        <CFormInput
                            type="color"
                            value={markerColor}
                            onChange={(e) => setMarkerColor(e.target.value)}
                            style={{ width: '40px', height: '30px', padding: 0 }}
                        />
                    </div>
                    {savedBoxes.length > 0 && (
                        <CButton
                            size="sm"
                            color="success"
                            onClick={handleSaveChanges}
                            disabled={isSaving}
                            style={{ marginLeft: 'auto' }}
                        >
                            <CIcon icon={cilCheck} />
                            {isSaving ? ' Сохранение...' : ' Сохранить изменения'}
                        </CButton>
                    )}
                </div>
                
                <CModalBody style={styles.modalBody}>
                    <div style={styles.leftPanel}>
                        <div style={{ padding: '10px', borderBottom: '1px solid #eee', fontSize: '14px', fontWeight: '500' }}>
                            Выбранные дефекты
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '5px' }}>
                            {selectedDefects.length === 0 ? (
                                <div style={{ color: '#999', fontSize: '13px', textAlign: 'center', marginTop: '20px' }}>
                                    Пока не выбрано ни одного дефекта
                                </div>
                            ) : (
                                <CTable hover size="sm">
                                    <CTableBody>
                                        {selectedDefects.map((item) => (
                                            <CTableRow
                                                key={item.id}
                                                onClick={() => handleDefectRowClick(item)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <CTableDataCell
                                                    style={{
                                                        backgroundColor: selectedDefectForDrawing?.id === item.id ? '#66A7F2' : '',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {item.firstLevelClass?.NameOut || item.ClassID_FirstLevel} <br/>
                                                    {item.secondLevelClass?.NameOut || item.ClassID_SecondLevel}
                                                </CTableDataCell>
                                                <CTableDataCell style={{ width: '30px', verticalAlign: 'middle' }}>
                                                    <CButton
                                                        size="sm"
                                                        color="danger"
                                                        variant="ghost"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeDefect(item.id);
                                                        }}
                                                        style={{ padding: '0 6px', fontSize: '12px' }}
                                                    >
                                                        ×
                                                    </CButton>
                                                </CTableDataCell>
                                            </CTableRow>
                                        ))}
                                    </CTableBody>
                                </CTable>
                            )}
                        </div>
                    </div>
                    
                    <div style={styles.centerPanel}>
                        {fullImageLoading ? (
                            <SpinnerComponent />
                        ) : fullImageSrc ? (
                            <div
                                ref={containerRef}
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    cursor: isDrawingMode ? 'crosshair' : 'default',
                                    userSelect: 'none'
                                }}
                                onMouseDown={handleMouseDown}
                            >
                                <img
                                    ref={imageRef}
                                    src={fullImageSrc}
                                    alt="Full size"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        transform: `rotate(${rotation}deg)`,
                                        display: 'block'
                                    }}
                                    draggable={false}
                                />
                                
                                {/* Отрисовка сохраненных рамок */}
                                {savedBoxes.map(box => {
                                    const isSelected = box.id === selectedBoxId;
                                    const boxStyle = getBoxStyle(box);
                                    
                                    // Если рамка выбрана, делаем рамку синей, иначе цветом маркера
                                    if (isSelected) {
                                        boxStyle.border = '2px solid #0000FF'; // Синий для выделенной
                                        boxStyle.zIndex = 15;
                                    }
                                    
                                    return (
                                        <div key={box.id} style={boxStyle}>
                                            <div style={{
                                                position: 'absolute',
                                                top: '-20px',
                                                left: '0',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                color: isSelected ? 'blue' : box.color,
                                                background: 'rgba(255,255,255,0.7)',
                                                padding: '2px 4px'
                                            }}>
                                                {box.defect}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Отрисовка ручек ресайза для выделенной рамки */}
                                {selectedBoxId && <ResizeHandles boxId={selectedBoxId} />}

                                {/* Отрисовка временной рамки при рисовании */}
                                {tempBox && (
                                    <div style={getBoxStyle({ ...tempBox, color: markerColor })} />
                                )}
                            </div>
                        ) : (
                            <div style={{ color: '#999' }}>Выберите фото справа</div>
                        )}
                    </div>
                    
                    <div style={styles.rightPanel}>
                        {loading ? (
                            <div style={{ padding: '20px' }}><SpinnerComponent /></div>
                        ) : photos.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                Нет изображений без дефектов
                            </div>
                        ) : (
                            photos.map((photo, index) => {
                                const isSelected = selectedPhoto?.path === photo.path;
                                return (
                                    <div key={`${photo.path}-${index}`} style={styles.thumbnailContainer}>
                                        <div
                                            style={{
                                                ...styles.thumbnailWrapper,
                                                border: isSelected ? '2px solid #0d6efd' : '1px solid #e0e0e0'
                                            }}
                                            onClick={() => handleThumbnailClick(photo)}
                                        >
                                            <img src={photo.url} alt={photo.name} style={styles.thumbnailImage} />
                                        </div>
                                        <div style={styles.thumbnailLabel} title={photo.name}>{photo.name}</div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </CModalBody>
            </CModal>
            
            <SettingsNeuralModule
                visible={settingsModalVisible}
                onClose={() => setSettingsModalVisible(false)}
                onDefectDoubleClick={handleDefectSelected}
                filterAiObjectID={objectData?.AiObjectID || null}
                filterLtype={objectData?.Ltype || null}
            />
            <style jsx>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </>
    );
};

const styles = {
    buttonBar: {
        display: 'flex', gap: '12px', padding: '8px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', alignItems: 'center'
    },
    modalBody: {
        height: 'calc(100vh - 150px)', display: 'flex', padding: 0, overflow: 'hidden'
    },
    leftPanel: {
        width: '300px', borderRight: '1px solid #dee2e6', backgroundColor: '#f8f9fa', display: 'flex', flexDirection: 'column'
    },
    centerPanel: {
        flex: 1, borderRight: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9f9f9', overflow: 'hidden'
    },
    rightPanel: {
        width: '280px', borderLeft: '1px solid #dee2e6', overflowY: 'auto', backgroundColor: '#fdfdfd'
    },
    thumbnailContainer: {
        marginBottom: '8px', borderBottom: '1px solid #eee', backgroundColor: 'white', padding: '4px', textAlign: 'center'
    },
    thumbnailWrapper: {
        width: '200px', height: '150px', margin: '0 auto', overflow: 'hidden', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    thumbnailImage: {
        width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer'
    },
    thumbnailLabel: {
        marginTop: '6px', fontSize: '11px', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
    }
};

export default AddDefectsModal;